import React from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { dsV2 } from '../../ds-v2-tokens';

/**
 * Bannière d'alerte sémantique du Design System V2.
 *
 * Présentation pure : la couleur découle du `tone` (jetons `dsV2`), l'icône et le contenu sont fournis
 * par le consommateur, et un slot `action` (lien/bouton) est aligné à droite. `onClick` rend toute la
 * bannière cliquable (ex. « Régulariser »). Remplace les bandeaux ad hoc re-stylés à la main.
 */
export type AlertBannerTone = 'danger' | 'warning' | 'info' | 'success';

export type AlertBannerProps = {
  tone?: AlertBannerTone;
  icon?: React.ReactNode;
  children: React.ReactNode;
  /** Élément d'action aligné à droite (lien/bouton). */
  action?: React.ReactNode;
  /** Rend toute la bannière cliquable. */
  onClick?: () => void;
  sx?: SxProps<Theme>;
};

const TONES: Record<AlertBannerTone, { bg: string; color: string; border: string }> = {
  danger: { bg: dsV2.color.rougeDim, color: dsV2.color.rougeDark, border: dsV2.color.rougeBrd },
  warning: { bg: dsV2.color.amberDim, color: dsV2.color.amberDark, border: dsV2.color.amberBrd },
  info: { bg: dsV2.color.bleuDim, color: dsV2.color.bleuDark, border: dsV2.color.bleuBrd },
  success: { bg: dsV2.color.vertDim, color: dsV2.color.vert, border: dsV2.color.vertBrd },
};

export const AlertBanner = ({ tone = 'info', icon, children, action, onClick, sx }: AlertBannerProps) => {
  const palette = TONES[tone];
  return (
    <Box
      role="alert"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        padding: '10px 14px',
        borderRadius: dsV2.radius.sm,
        fontSize: 12,
        fontWeight: 500,
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'filter .15s',
        ...(onClick ? { '&:hover': { filter: 'brightness(0.98)' } } : {}),
        ...sx,
      }}
    >
      {icon ? <Box sx={{ display: 'flex', flexShrink: 0 }}>{icon}</Box> : null}
      <Box component="span" sx={{ minWidth: 0 }}>
        {children}
      </Box>
      {action ? <Box sx={{ marginLeft: 'auto', flexShrink: 0, display: 'inline-flex' }}>{action}</Box> : null}
    </Box>
  );
};

export default AlertBanner;
